function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useKeyPress, useOnClickOutside, Button } from '../../index';
export const useDrawer = (initialActiveMode = false, initialCollapseMode = false) => {
  const [drawerActive, setDrawerActive] = useState(initialActiveMode);
  const [drawerCollapsed, setDrawerCollapsed] = useState(initialCollapseMode);

  const setNextDrawerActive = nextDrawerActive => {
    if (nextDrawerActive === drawerActive === true && drawerCollapsed) {
      return setDrawerCollapsed(false);
    }

    setDrawerActive(nextDrawerActive);
  };

  const ownerDocument = node => node && node.ownerDocument || document;

  const mountNodeRef = useRef(null);
  const documentBody = ownerDocument(mountNodeRef.current).body;
  useEffect(() => {
    if (drawerActive === true && drawerCollapsed === false) {
      documentBody.style.overflowY = 'hidden';
    }

    if (drawerActive === false || drawerCollapsed === true) {
      documentBody.style.overflowY = 'visible';
    }
  }, [drawerActive, drawerCollapsed]);
  return [drawerActive, setNextDrawerActive, drawerCollapsed, setDrawerCollapsed];
};
export const Drawer = ({
  isActive,
  children,
  handleClose,
  handleCollapse,
  handleExpand,
  isCollapsed = null,
  collapseLabel,
  width = '25rem',
  isOutsideClickEnabled = true,
  isEscKeyEnabled = true,
  className = '',
  style = {}
}) => {
  const ref = useRef();
  if (isEscKeyEnabled && useKeyPress('Escape')) handleClose();

  if (isOutsideClickEnabled) {
    useOnClickOutside(ref, handleCollapse ? () => handleCollapse() : () => handleClose());
  }

  return isActive ? /*#__PURE__*/React.createElement("div", {
    className: classnames(className, 'drawer', {
      'drawer--collapsed': isCollapsed
    }),
    style: style
  }, /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: isCollapsed ? 'drawer__container drawer__container--collapsed' : 'drawer__container',
    style: isCollapsed ? {} : {
      width
    }
  }, isCollapsed !== null && /*#__PURE__*/React.createElement(DrawerCollapseHeader, {
    isCollapsed: isCollapsed,
    handleCollapse: handleCollapse,
    handleExpand: handleExpand,
    label: collapseLabel,
    width: width
  }), children)) : null;
};
export const DrawerCollapseHeader = ({
  label,
  isCollapsed,
  handleCollapse,
  handleExpand,
  width = '25rem',
  className = '',
  style = {},
  ...rest
}) => {
  return /*#__PURE__*/React.createElement(Button, _extends({
    version: "primary",
    label: label || '',
    icon: isCollapsed ? 'UpOpen' : 'DownOpen',
    className: classnames(className, 'drawer__collapse-button', {
      'drawer__collapse-button--collapsed': isCollapsed
    }),
    onClick: isCollapsed ? handleExpand : handleCollapse,
    style: isCollapsed ? style : {
      width,
      ...style
    }
  }, rest));
};
export const DrawerHeader = ({
  children,
  className,
  ...props
}) => {
  return /*#__PURE__*/React.createElement("div", _extends({
    className: `drawer__header ${className}`
  }, props), children);
};
export const DrawerBody = ({
  children,
  className,
  ...props
}) => {
  return /*#__PURE__*/React.createElement("div", _extends({
    className: `drawer__content ${className}`
  }, props), children);
};
export const DrawerFooter = ({
  children,
  className,
  ...props
}) => {
  return /*#__PURE__*/React.createElement("div", _extends({
    className: `drawer__footer ${className}`
  }, props), children);
};
Drawer.propTypes = {
  isActive: PropTypes.bool.isRequired,
  handleClose: PropTypes.func,
  handleCollapse: PropTypes.func,
  handleExpand: PropTypes.func,
  isCollapsed: PropTypes.bool,
  collapseLabel: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  style: PropTypes.object
};